import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import CheckboxTree from 'react-checkbox-tree';
import 'react-checkbox-tree/lib/react-checkbox-tree.css';
import { Checkbox } from '@agconnections/grow-ui';
import {
  ArrowRight,
  ArrowDropDown,
  CheckBox,
  CheckBoxOutlineBlank,
  IndeterminateCheckBox
} from '@material-ui/icons';
import ChecklistProperty from 'screens/CropSeasons/CropSeason/components/FormSection/GroupByFilterProperty';

const GROUP_BY_CROP = 'crop';

const checkboxIcons = {
  halfCheck: <IndeterminateCheckBox fontSize="small" />,
  uncheck: (
    <CheckBoxOutlineBlank fontSize="small" className="text-neutral-600" />
  ),
  check: <CheckBox fontSize="small" />,
  expandClose: <ArrowRight fontSize="small" className="text-neutral-600" />,
  expandOpen: <ArrowDropDown fontSize="small" className="text-neutral-600" />
};

const mapCropzones = (cropzones, parentArchived) =>
  (cropzones || []).map(cz => ({
    value: cz.id,
    label: cz.name + (cz.archived ? ' (archived)' : ''),
    className: cz.archived && !parentArchived ? 'opacity-50' : '',
    type: 'cropzone'
  }));

const mapFields = (fields, cropId, farmId, parentArchived) =>
  (fields || []).map(field => ({
    value: `crop_${cropId}_farm_${farmId}_field_${field.id}`,
    label: field.name + (field.archived ? ' (archived)' : ''),
    className: field.archived && !parentArchived ? 'opacity-50' : '',
    type: 'field',
    children: mapCropzones(field.cropzones, field.archived)
  }));

const mapFarms = (farms, cropId) =>
  (farms || []).map(farm => ({
    value: `crop_${cropId}_farm_${farm.id}`,
    label: farm.name + (farm.archived ? ' (archived)' : ''),
    className: farm.archived ? 'opacity-50' : '',
    type: 'farm',
    children: mapFields(farm.fields, cropId, farm.id, farm.archived)
  }));

const mapCropGroupToNodes = cropGroups =>
  cropGroups.map(crop => ({
    value: `crop_${crop.id}`,
    label: crop.name,
    type: 'crop',
    children: mapFarms(crop.farms, crop.id)
  }));

const getAllCropzoneIds = nodes => {
  const ids = [];
  const traverse = nodeList => {
    nodeList.forEach(node => {
      if (node.type === 'cropzone') {
        ids.push(node.value);
      } else if (node.children) {
        traverse(node.children);
      }
    });
  };
  traverse(nodes);
  return ids;
};

const CropGroupSelector = ({
  properties,
  cropZonesChecked,
  setCropZonesChecked,
  onSelectAll = undefined
}) => {
  const [expanded, setExpanded] = useState([]);

  const nodes = useMemo(() => mapCropGroupToNodes(properties), [properties]);

  const allCropzoneIds = useMemo(() => getAllCropzoneIds(nodes), [nodes]);

  const isAllSelected =
    allCropzoneIds.length > 0 &&
    allCropzoneIds.every(id => cropZonesChecked.includes(id));

  const handleSelectAll = () => {
    if (isAllSelected) {
      setCropZonesChecked([]);
    } else if (onSelectAll) {
      onSelectAll(allCropzoneIds);
    } else {
      setCropZonesChecked(allCropzoneIds);
    }
  };

  const handleCheck = useCallback(
    checkedIds => {
      // react-checkbox-tree returns all checked values including intermediate nodes
      // Filter to only keep cropzone IDs (leaf nodes without the composite prefix)
      const cropzoneIds = checkedIds.filter(id => !id.startsWith('crop_'));
      setCropZonesChecked(cropzoneIds);
    },
    [setCropZonesChecked]
  );

  // Build checked array including intermediate nodes for react-checkbox-tree
  // The library needs leaf nodes in checked to properly cascade up
  const checkedForTree = cropZonesChecked;

  return (
    <div
      className="filter-container"
      data-testid="crop-group-property-selector"
    >
      <div className="mt-2 select-all-checkbox-wrapper">
        <Checkbox
          data-testid="select-all-properties-checkbox"
          label="Select All"
          onChange={handleSelectAll}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSelectAll();
            }
          }}
          checked={isAllSelected}
          color="#0071cd"
          tabIndex={0}
        />
      </div>
      <CheckboxTree
        nodes={nodes}
        checked={checkedForTree}
        showNodeIcon={false}
        icons={checkboxIcons}
        expanded={expanded}
        onCheck={handleCheck}
        onExpand={setExpanded}
        showNodeTitle
        optimisticToggle={false}
      />
    </div>
  );
};

CropGroupSelector.propTypes = {
  properties: PropTypes.arrayOf(PropTypes.object).isRequired,
  cropZonesChecked: PropTypes.arrayOf(PropTypes.string).isRequired,
  setCropZonesChecked: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func
};

const PropertySelector = ({
  properties,
  setCropZonesChecked,
  cropZonesChecked,
  groupBy = 'farm',
  onSelectAll = undefined
}) => {
  if (groupBy === GROUP_BY_CROP) {
    return (
      <CropGroupSelector
        properties={properties}
        cropZonesChecked={cropZonesChecked}
        setCropZonesChecked={setCropZonesChecked}
        onSelectAll={onSelectAll}
      />
    );
  }

  return (
    <ChecklistProperty
      data={properties}
      cropZonesChecked={cropZonesChecked}
      setCropZonesChecked={setCropZonesChecked}
      disabled={false}
      onSelectAll={onSelectAll}
    />
  );
};

PropertySelector.propTypes = {
  cropZonesChecked: PropTypes.arrayOf(PropTypes.string).isRequired,
  properties: PropTypes.arrayOf(PropTypes.object).isRequired,
  setCropZonesChecked: PropTypes.func.isRequired,
  groupBy: PropTypes.string,
  onSelectAll: PropTypes.func
};

export default PropertySelector;
